import Button, { type ButtonSize } from 'components/Button';
import { JURIDICAL_TYPE_QUERY_PARAM } from 'constants/queryParams';
import { useIsCompanySite, useIsEditing, useIsPreviewing } from 'hooks';
import { useSearchParams } from 'hooks/navigation';
import type { JuridicalType } from 'models/sitecore';
import { cn } from 'utils/classNames';
import { empty } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { parseUrl } from 'utils/url';

import { useSiteSwitcher } from './helpers';

interface Props {
	className?: string;
	size: ButtonSize;
	variant: 'base' | 'branded' | 'positive';
}

interface ButtonsProps extends Props {
	isCompanySite: boolean;
	onClick: (juridicalType: JuridicalType) => void;
}

export function Buttons({
	className,
	isCompanySite,
	onClick,
	size,
	variant,
}: ButtonsProps) {
	const { t } = useI18n();

	const items: { isActive: boolean; juridicalType: JuridicalType }[] = [
		{ isActive: !isCompanySite, juridicalType: 'Private' },
		{ isActive: isCompanySite, juridicalType: 'Company' },
	];

	return (
		<ul
			className={cn(
				'inline-flex gap-0.5 rounded-full',
				variant === 'branded' && 'bg-opacityDarken',
				variant === 'positive' && 'bg-opacityLighten',
				variant === 'base' && 'bg-subtle',
				className,
			)}
		>
			{items.map(({ isActive, juridicalType }) => (
				<li key={juridicalType}>
					<Button
						size={size}
						onClick={isActive ? empty.func : () => onClick(juridicalType)}
						disabled={isActive}
						variant={isActive ? 'secondary' : 'borderless'}
						className={cn(
							'whitespace-nowrap',
							isActive && [
								// Override disabled colors. The active item is disabled
								// for accessibility and to disable hover feedback, but
								// it shouldn't be dimmed visually.
								'border-strong text-base',
								// Differentiate active with a visually thicker border in
								// forced colors mode.
								'forced-colors:not-focus-visible:outline-1',
								(variant === 'branded' || variant === 'positive') &&
									'border-transparent focus-visible:focus-outline-inverted',
							],
							!isActive && [
								variant === 'branded' && 'text-negative hover:bg-opacityDarken',
								variant === 'positive' &&
									'text-negative hover:bg-opacityLighten',
							],
						)}
					>
						{t(
							juridicalType === 'Company'
								? 'site_switcher_company_label'
								: 'site_switcher_private_label',
						)}
					</Button>
				</li>
			))}
		</ul>
	);
}
Buttons.displayName = 'SiteSwitcher_Buttons';

export default function SiteSwitcher({ className, size, variant }: Props) {
	const { switchSite } = useSiteSwitcher();
	const searchParams = useSearchParams();
	const queryJuridicalType = searchParams.get('juridicaltype');
	const isEditing = useIsEditing();
	const isPreviewing = useIsPreviewing();
	const isCompanySiteDomain = useIsCompanySite();
	const isCompanySite =
		isEditing || isPreviewing
			? queryJuridicalType === 'Company'
			: isCompanySiteDomain;

	if (!switchSite) {
		return null;
	}

	return (
		<Buttons
			className={className}
			size={size}
			isCompanySite={isCompanySite}
			onClick={
				isEditing || isPreviewing
					? (juridicalType) => {
							const url = parseUrl(globalThis.location.href);
							if (url) {
								url.searchParams.set(JURIDICAL_TYPE_QUERY_PARAM, juridicalType);
								url.searchParams.set('sc_mode', 'preview');
								globalThis.location.replace(url.toString());
							}
						}
					: switchSite
			}
			variant={variant}
		/>
	);
}
SiteSwitcher.displayName = 'SiteSwitcher';
